import { useEffect, useRef, useState } from 'react';
import { Button, Checkbox, Select } from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  isSeriesBranchRule,
  type CategoryRateTier,
  type SeriesBranchRateRule,
  type SeriesRateRule,
  type SeriesSlotRateRule,
} from '@salaries/shared';
import { getClassCatalog, getRateCatalog, type SeriesCatalogEntry } from '../../api/classCatalog';
import { FLAT_TIER, RateTiersEditor } from './RateTiersEditor';
import { DuplicatePanel } from './DuplicatePanel';

// Editor for the series rates: one recurring class slot pays its own ₪ per
// class, beating the קטגוריה rates and Arbox's pay.
// A series is picked (and matched) by Arbox's series_id; the slot saved with it
// is display only. Rules saved before the id existed are keyed by slot.

type Slot = Pick<SeriesSlotRateRule, 'series_id' | 'class_name' | 'location' | 'weekday' | 'start_time' | 'series_name'>;
type Row = { id: string; slot: Slot | null; tiers: CategoryRateTier[] };

const DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const slotKey = (s: Slot) =>
  s.series_id != null ? `#${s.series_id}` : JSON.stringify([s.class_name, s.location, s.weekday, s.start_time]);
const slotLabel = (s: Slot) => `${s.class_name} · ${s.location} · ${DAYS[s.weekday]} ${s.start_time}`;

let ridSeq = 0;
const newId = () => `s${ridSeq++}`;

const toRows = (rules: SeriesSlotRateRule[]): Row[] =>
  rules.map((r) => ({ id: newId(), slot: r, tiers: r.tiers }));

// Half-filled rows (no series picked yet) stay in the editor but are never saved.
const toRules = (rows: Row[]): SeriesSlotRateRule[] =>
  rows
    .filter((r): r is Row & { slot: Slot } => !!r.slot && r.tiers.length > 0)
    .map((r) => ({ ...r.slot, tiers: r.tiers }));

// value/onChange are injected by the wrapping <Form.Item name="series_rates">.
// The list holds two kinds of rule: a rate for every series at a branch, and a
// rate for one series (which beats the branch rate). Each kind has its own editor.
// staffId (coach drawer) narrows the series picker to the series this coach taught.
export function SeriesRateFields({
  value = [],
  onChange,
  staffId,
}: {
  value?: SeriesRateRule[];
  onChange?: (v: SeriesRateRule[]) => void;
  staffId?: number;
}) {
  const branchRules = value.filter(isSeriesBranchRule);
  const slotRules = value.filter((r): r is SeriesSlotRateRule => !isSeriesBranchRule(r));
  return (
    <div>
      <div className="font-medium mb-1">תעריף לפי סניף</div>
      <SeriesBranchRateFields value={branchRules} onChange={(b) => onChange?.([...b, ...slotRules])} />
      <div className="font-medium mt-5 mb-1">תעריף לשיעור קבוע מסוים</div>
      <SeriesSlotRateFields
        value={slotRules}
        onChange={(sl) => onChange?.([...branchRules, ...sl])}
        staffId={staffId}
      />
    </div>
  );
}

type BranchRow = { id: string; location: string | null; tiers: CategoryRateTier[] };

// Rows without a branch stay in the editor but are never saved.
const branchToRules = (rows: BranchRow[]): SeriesBranchRateRule[] =>
  rows
    .filter((r): r is BranchRow & { location: string } => !!r.location && r.tiers.length > 0)
    .map((r) => ({ scope: 'branch', location: r.location, tiers: r.tiers }));

function SeriesBranchRateFields({
  value,
  onChange,
}: {
  value: SeriesBranchRateRule[];
  onChange: (v: SeriesBranchRateRule[]) => void;
}) {
  const catalogQ = useQuery({ queryKey: ['class-catalog'], queryFn: getClassCatalog });
  const toBranchRows = (rules: SeriesBranchRateRule[]): BranchRow[] =>
    rules.map((r) => ({ id: newId(), location: r.location, tiers: r.tiers }));
  const [rows, setRows] = useState<BranchRow[]>(() => toBranchRows(value));
  const lastSent = useRef<string>(JSON.stringify(value));
  useEffect(() => {
    const incoming = JSON.stringify(value);
    if (incoming !== lastSent.current) {
      lastSent.current = incoming;
      setRows(toBranchRows(value));
    }
  }, [value]);

  const commit = (next: BranchRow[]) => {
    setRows(next);
    const rules = branchToRules(next);
    lastSent.current = JSON.stringify(rules);
    onChange(rules);
  };
  const patch = (id: string, p: Partial<BranchRow>) => commit(rows.map((r) => (r.id === id ? { ...r, ...p } : r)));

  // A saved branch may no longer appear in recent meetings — keep it pickable.
  const locations = new Set(catalogQ.data?.locations ?? []);
  for (const r of rows) if (r.location) locations.add(r.location);
  const locOptions = [...locations].map((l) => ({ value: l, label: l }));
  const taken = (row: BranchRow) => !!row.location && rows.some((o) => o.id !== row.id && o.location === row.location);

  return (
    <div>
      <div className="text-small text-secondary-text mb-2">
        תעריף אחד לכל השיעורים הקבועים בסניף, במקום תעריף הקטגוריה והתעריף שבארבוקס. שיעור קבוע חדש
        בסניף מקבל את התעריף אוטומטית. שיעור קבוע שעובר לסניף אחר משולם לפי התעריף של הסניף החדש. שיעור
        קבוע שהוגדר לו תעריף משלו (למטה) משולם לפי התעריף שלו. שיעורים חד-פעמיים לא מקבלים את התעריף הזה.
      </div>
      {rows.map((r) => (
        <div key={r.id} className="border rounded px-3 py-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-small text-secondary-text">סניף</span>
            <Select
              showSearch
              placeholder="בחרו סניף"
              style={{ minWidth: 240, flex: 1 }}
              value={r.location ?? undefined}
              onChange={(v) => patch(r.id, { location: v })}
              options={locOptions}
              optionFilterProp="label"
              loading={catalogQ.isLoading}
              notFoundContent={catalogQ.isLoading ? 'טוען…' : 'לא נמצאו סניפים'}
            />
            <Button size="small" danger type="text" onClick={() => commit(rows.filter((o) => o.id !== r.id))}>
              הסר
            </Button>
          </div>
          {taken(r) && (
            <div className="text-small text-error-red mt-1">כבר קיים תעריף לסניף הזה — רק אחד מהם יחול.</div>
          )}
          <RateTiersEditor tiers={r.tiers} onChange={(tiers) => patch(r.id, { tiers })} />
        </div>
      ))}
      <Button onClick={() => commit([...rows, { id: newId(), location: null, tiers: [FLAT_TIER] }])}>
        + תעריף לסניף
      </Button>
    </div>
  );
}

function SeriesSlotRateFields({
  value = [],
  onChange,
  staffId,
}: {
  value?: SeriesSlotRateRule[];
  onChange?: (v: SeriesSlotRateRule[]) => void;
  staffId?: number;
}) {
  const rateCatalogQ = useQuery({ queryKey: ['rate-catalog'], queryFn: getRateCatalog });
  const [showAll, setShowAll] = useState(staffId == null);

  const [rows, setRows] = useState<Row[]>(() => toRows(value));
  const lastSent = useRef<string>(JSON.stringify(value));
  useEffect(() => {
    const incoming = JSON.stringify(value);
    if (incoming !== lastSent.current) {
      lastSent.current = incoming;
      setRows(toRows(value));
    }
  }, [value]);

  const commit = (next: Row[]) => {
    setRows(next);
    const rules = toRules(next);
    lastSent.current = JSON.stringify(rules);
    onChange?.(rules);
  };
  const patch = (id: string, p: Partial<Row>) => commit(rows.map((r) => (r.id === id ? { ...r, ...p } : r)));

  const catalog = rateCatalogQ.data?.series ?? [];
  const visible = showAll || staffId == null ? catalog : catalog.filter((s) => s.staff_ids.includes(staffId));
  // A saved rule's slot may no longer appear in recent meetings — keep it pickable.
  const bySlot = new Map<string, Slot & Partial<Pick<SeriesCatalogEntry, 'meetings'>>>();
  for (const s of visible) bySlot.set(slotKey(s), s);
  for (const r of rows) if (r.slot && !bySlot.has(slotKey(r.slot))) bySlot.set(slotKey(r.slot), r.slot);
  const options = [...bySlot.entries()].map(([k, s]) => ({
    value: k,
    label: s.meetings != null ? `${slotLabel(s)} (${s.meetings} שיעורים)` : `${slotLabel(s)} — לא נמצא לאחרונה`,
  }));
  const taken = (row: Row) =>
    !!row.slot && rows.some((o) => o.id !== row.id && o.slot && slotKey(o.slot) === slotKey(row.slot!));

  // שכפול: copy one row's tiers to more series. A series that already has a row
  // gets its tiers overwritten, so no series ends up twice.
  const [dup, setDup] = useState<{ id: string; keys: string[] } | null>(null);
  const planDup = () => {
    const src = rows.find((r) => r.id === dup?.id);
    if (!dup || !src?.slot) return { next: rows, added: 0, replaced: 0 };
    let next = rows;
    let added = 0;
    let replaced = 0;
    for (const k of dup.keys) {
      const s = bySlot.get(k);
      if (!s || k === slotKey(src.slot)) continue;
      const same = (o: Row) => o.id !== src.id && !!o.slot && slotKey(o.slot) === k;
      const tiers = src.tiers.map((t) => ({ ...t }));
      if (next.some(same)) {
        replaced++;
        next = next.map((o) => (same(o) ? { ...o, tiers } : o));
      } else {
        added++;
        const slot: Slot = { series_id: s.series_id, class_name: s.class_name, location: s.location, weekday: s.weekday, start_time: s.start_time, series_name: s.series_name };
        next = [...next, { id: newId(), slot, tiers }];
      }
    }
    return { next, added, replaced };
  };
  const dupPlan = planDup();

  return (
    <div>
      <div className="text-small text-secondary-text mb-2">
        תעריף לשיעור קבוע מסוים (סוג שיעור, סניף, יום ושעה), במקום תעריף הסניף, תעריף הקטגוריה והתעריף שבארבוקס.
        שיעור קבוע שהוגדר לו תעריף ישולם לפי התעריף כאן בלבד, גם אם שעת השיעור, היום או הסניף משתנים
        בארבוקס.
      </div>
      {staffId != null && (
        <Checkbox className="mb-2" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}>
          הצג את כל השיעורים הקבועים בעסק (לא רק של איש צוות זה)
        </Checkbox>
      )}

      {rows.map((r) => (
        <div key={r.id} className="border rounded px-3 py-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-small text-secondary-text">שיעור קבוע</span>
            <Select
              showSearch
              placeholder="בחרו שיעור קבוע"
              style={{ minWidth: 320, flex: 1 }}
              value={r.slot ? slotKey(r.slot) : undefined}
              onChange={(k) => {
                const s = bySlot.get(k);
                if (s) patch(r.id, { slot: { series_id: s.series_id, class_name: s.class_name, location: s.location, weekday: s.weekday, start_time: s.start_time, series_name: s.series_name } });
              }}
              options={options}
              optionFilterProp="label"
              loading={rateCatalogQ.isLoading}
              notFoundContent={rateCatalogQ.isLoading ? 'טוען…' : 'לא נמצאו שיעורים קבועים'}
            />
            <Button size="small" type="text" disabled={!r.slot} onClick={() => setDup({ id: r.id, keys: [] })}>
              שכפול
            </Button>
            <Button size="small" danger type="text" onClick={() => commit(rows.filter((o) => o.id !== r.id))}>
              הסר
            </Button>
          </div>
          {taken(r) && (
            <div className="text-small text-error-red mt-1">כבר קיים תעריף לשיעור הקבוע הזה — רק אחד מהם יחול.</div>
          )}
          <RateTiersEditor tiers={r.tiers} onChange={(tiers) => patch(r.id, { tiers })} />
          {dup?.id === r.id && r.slot && (
            <DuplicatePanel
              added={dupPlan.added}
              replaced={dupPlan.replaced}
              onApply={() => {
                commit(dupPlan.next);
                setDup(null);
              }}
              onCancel={() => setDup(null)}
            >
              <span className="text-small text-secondary-text">לשיעורים קבועים</span>
              <Select
                mode="multiple"
                showSearch
                placeholder="בחרו שיעורים קבועים"
                style={{ minWidth: 320, flex: 1 }}
                value={dup.keys}
                onChange={(keys) => setDup({ ...dup, keys })}
                options={options.filter((o) => o.value !== slotKey(r.slot!))}
                optionFilterProp="label"
              />
            </DuplicatePanel>
          )}
        </div>
      ))}

      <Button onClick={() => commit([...rows, { id: newId(), slot: null, tiers: [FLAT_TIER] }])}>
        + תעריף לשיעור קבוע
      </Button>
    </div>
  );
}
